import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';
import { getCategories } from '../../../../services/products/productService';
import {
  ProductDataSource,
  ProductData,
} from '../../../../datasources/catalog/ProductsDataSource';
import type { Product } from '../../../../types/product/Product';
import { BrighterGreen, DarkGreen } from '../../../../app/theme/tokens/colors';

import { SubCategoryCard } from '../../../orders/components/new-order/subCategoryCard';
import { usePortalStore } from '../../../../stores/portalStore';
import type { PatientV2Detail } from './types';
import { PatientEquipmentQuestionnaire } from './PatientEquipmentQuestionnaire';

type Therapy = {
  id: number;
  description: string;
  title?: string;
  coverImageURL?: string;
  shortDescription?: string;
};
const PAGE_SIZE = 1000;
const PLACEHOLDER = '/icones/placeholder-image.png';
const CatalogImage = ({
  source,
  name,
  contain = false,
}: {
  source: string;
  name: string;
  contain?: boolean;
}) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [source]);
  return failed ? (
    <Box
      role="img"
      aria-label={`Imagem indisponível: ${name}`}
      sx={{
        height: 160,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#fafcfb',
        color: '#9eb7a2',
      }}
    >
      <ImageOutlined sx={{ fontSize: 32 }} />
    </Box>
  ) : (
    <Box
      component="img"
      src={source}
      alt={name}
      onError={() => setFailed(true)}
      sx={{
        width: '100%',
        height: 160,
        flexShrink: 0,
        objectFit: contain ? 'contain' : 'cover',
        objectPosition: 'top center',
        display: 'block',
        p: contain ? 1.5 : 0,
        boxSizing: 'border-box',
        bgcolor: '#fafcfb',
      }}
    />
  );
};
const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
const therapyVisuals = [
  {
    match: 'ventilacao domiciliar/linha do sono',
    title: 'Linha do sono',
    folder: 'linhaDoSono',
    description: 'Equipamentos para tratamento do distúrbio do sono',
  },
  {
    match: 'oxige',
    title: 'Oxigenoterapia',
    folder: 'oxigenoterapia',
    description: 'Equipamentos para tratamento com oxigênio',
  },
  {
    match: 'domici',
    title: 'Ventilação domiciliar',
    folder: 'ventilacaoDomiciliar',
    description:
      'Equipamentos para terapias respiratórias invasivas e não invasivas',
  },
  {
    match: 'sono',
    title: 'Linha do sono',
    folder: 'linhaDoSono',
    description: 'Equipamentos para tratamento do distúrbio do sono',
  },
  {
    match: 'metros',
    title: 'Oxímetros',
    folder: 'oximetros',
    description: 'Monitoramento da saturação de oxigênio e frequência cardíaca',
  },
  {
    match: 'pirador',
    title: 'Aspiradores de secreção',
    folder: 'aspiradorDeSecrecao',
    description: 'Equipamentos para auxiliar a aspiração de secreção',
  },
];

export const PatientEquipmentSelection = ({
  patient,
}: {
  patient: PatientV2Detail;
}) => {
  const currentUser = usePortalStore(
    (state) =>
      (state as { currentUser: { canChooseEquipmentSubCateogry: boolean } })
        .currentUser
  );
  const [therapies, setTherapies] = useState<Therapy[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(false);
  const [categoryAttempt, setCategoryAttempt] = useState(0);
  const [therapy, setTherapy] = useState<Therapy | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [selection, setSelection] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const therapyCards = useRef(new Map<number, HTMLDivElement>());
  const movingTherapyId = useRef<number | null>(null);
  const galleryScroll = useRef(0);
  const previousRect = useRef<DOMRect | null>(null);
  const productCards = useRef(new Map<string, HTMLDivElement>());
  const productStrip = useRef<HTMLDivElement>(null);
  const productScroll = useRef(0);
  const productMovement = useRef<{
    groupId: string;
    rect: DOMRect;
    returning: boolean;
  } | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    let current = true;
    setCategoriesLoading(true);
    setCategoriesError(false);
    getCategories(null, 0, 1000, { bypassCache: categoryAttempt > 0 })
      .then((response) => {
        if (!current) return;
        if (!Array.isArray(response?.data))
          throw new Error('Catálogo inválido');
        setTherapies(
          response.data.filter((category: Therapy) => {
            if (
              !category ||
              category.id == null ||
              typeof category.description !== 'string'
            )
              return false;
            const text = normalize(
              `${category.title ?? ''} ${category.description ?? ''}`
            );
            return (
              !/\bcircuitos?\s+respiratorios?\b/.test(text) &&
              !text.includes('descartaveis para equipamentos medicos') &&
              !text.includes(
                'dispositivos e acessorios de equipamentos medicos'
              )
            );
          })
        );
      })
      .catch(() => {
        if (current) setCategoriesError(true);
      })
      .finally(() => {
        if (current) setCategoriesLoading(false);
      });
    return () => {
      current = false;
    };
  }, [categoryAttempt]);

  useEffect(() => {
    if (!therapy) return;
    let current = true;
    setLoading(true);
    setError(false);
    const loadProducts = async () => {
      const source = new ProductDataSource();
      const allProducts = new Map<number, Product>();
      let page = 0;
      let received = 0;
      let total = 0;
      do {
        const response = await source.QueryProducts(
          null,
          null,
          null,
          therapy.id,
          null,
          null,
          'S',
          null,
          null,
          page,
          PAGE_SIZE,
          ['Equipamento', 'Equipamentos', 'Cilindro', 'Cilindros']
        );
        if (!current) return;
        const result = response as ProductData;
        if (!Array.isArray(result?.data)) throw new Error('Catálogo inválido');
        total = Number(result.rows);
        if (
          !Number.isFinite(total) ||
          total < 0 ||
          (result.data.length === 0 && received < total)
        ) {
          throw new Error('Catálogo incompleto');
        }
        result.data.forEach((product) => allProducts.set(product.id, product));
        received += result.data.length;
        page += 1;
      } while (received < total);
      if (current) setProducts(Array.from(allProducts.values()));
    };
    loadProducts()
      .catch(() => {
        if (current) setError(true);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [therapy, attempt]);

  useLayoutEffect(() => {
    const id = movingTherapyId.current;
    const node = id === null ? undefined : therapyCards.current.get(id);
    const before = previousRect.current;
    movingTherapyId.current = null;
    previousRect.current = null;
    if (!therapy && strip.current)
      strip.current.scrollLeft = galleryScroll.current;
    node?.querySelector('button')?.focus({ preventScroll: true });
    if (!node || !before || reduceMotion || !node.animate) return;
    const after = node.getBoundingClientRect();
    const offset = before.left - after.left;
    const horizontalOffset =
      Math.abs(offset) < 1 ? (therapy ? 32 : -32) : offset;
    const animation = node.animate(
      [
        {
          transform: `translate(${horizontalOffset}px, ${
            before.top - after.top
          }px)`,
        },
        { transform: 'translate(0, 0)' },
      ],
      { duration: 250, easing: 'ease-out' }
    );
    return () => animation.cancel();
  }, [therapy, reduceMotion]);

  const productGroups = useMemo(() => {
    const groups = new Map<
      string,
      { id: string; description: string; produtos: Product[] }
    >();
    products.forEach((product) => {
      const key =
        product.product_subcategory_id > 0
          ? `subcategory-${product.product_subcategory_id}`
          : `product-${product.id}`;
      if (!groups.has(key))
        groups.set(key, {
          id: key,
          description:
            product.product_subcategory?.description ?? product.description,
          produtos: [],
        });
      groups.get(key)!.produtos.push(product);
    });
    return Array.from(groups.values()).filter(
      (group) => !['Não se aplica', 'Cilindros'].includes(group.description)
    );
  }, [products]);

  useLayoutEffect(() => {
    const movement = productMovement.current;
    productMovement.current = null;
    if (!movement) return;
    if (productStrip.current)
      productStrip.current.scrollLeft = movement.returning
        ? productScroll.current
        : 0;
    const node = productCards.current.get(movement.groupId);
    node?.querySelector('button')?.focus({ preventScroll: true });
    if (!node || reduceMotion || !node.animate) return;
    const after = node.getBoundingClientRect();
    const delta = movement.rect.left - after.left;
    const horizontal =
      Math.abs(delta) < 1 ? (movement.returning ? -32 : 32) : delta;
    const animation = node.animate(
      [
        {
          transform: `translate(${horizontal}px, ${
            movement.rect.top - after.top
          }px)`,
        },
        { transform: 'translate(0, 0)' },
      ],
      { duration: 250, easing: 'ease-out' }
    );
    return () => animation.cancel();
  }, [selection, reduceMotion]);

  const selectProduct = (product: Product) => {
    if (!selection) {
      const group = productGroups.find((item) =>
        item.produtos.some((entry) => entry.id === product.id)
      );
      const node = group && productCards.current.get(group.id);
      if (group && node)
        productMovement.current = {
          groupId: group.id,
          rect: node.getBoundingClientRect(),
          returning: false,
        };
      productScroll.current = productStrip.current?.scrollLeft ?? 0;
    }
    setSelection(product);
  };

  const closeProduct = () => {
    const group = productGroups.find((item) =>
      item.produtos.some((entry) => entry.id === selection?.id)
    );
    const node = group && productCards.current.get(group.id);
    if (group && node)
      productMovement.current = {
        groupId: group.id,
        rect: node.getBoundingClientRect(),
        returning: true,
      };
    setSelection(null);
  };

  const openTherapy = (item: Therapy, node: HTMLElement) => {
    previousRect.current = (
      therapyCards.current.get(item.id) ?? node
    ).getBoundingClientRect();
    movingTherapyId.current = item.id;
    galleryScroll.current = strip.current?.scrollLeft ?? 0;
    setSelection(null);
    productMovement.current = null;
    setProducts([]);
    setLoading(true);
    setError(false);
    setTherapy(item);
    if (strip.current) strip.current.scrollLeft = 0;
  };

  const closeTherapy = () => {
    if (!therapy) return;
    previousRect.current =
      therapyCards.current.get(therapy.id)?.getBoundingClientRect() ?? null;
    movingTherapyId.current = therapy.id;
    setSelection(null);
    productMovement.current = null;
    setTherapy(null);
  };

  const therapyCard = (item: Therapy, active = false) => {
    const visual = therapyVisuals.find((entry) =>
      normalize(item.description).includes(entry.match)
    );
    return (
      <Card
        key={item.id}
        ref={(node: HTMLDivElement | null) => {
          if (node) therapyCards.current.set(item.id, node);
          else therapyCards.current.delete(item.id);
        }}
        variant="outlined"
        sx={{
          width: 260,
          height: 344,
          flexShrink: 0,
          borderRadius: 2,
          borderColor: active ? BrighterGreen : '#dce5de',
          alignSelf: 'flex-start',
          position: 'relative',
          m: active ? 0.5 : 0,
        }}
      >
        <CardActionArea
          component="button"
          type="button"
          aria-label={
            active
              ? 'Trocar terapia'
              : `Abrir ${item.title ?? visual?.title ?? item.description}`
          }
          onClick={(event) =>
            active ? closeTherapy() : openTherapy(item, event.currentTarget)
          }
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            textAlign: 'left',
            '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
            '&:hover': { bgcolor: '#f6faf5' },
          }}
        >
          <CatalogImage
            name={item.title ?? visual?.title ?? item.description}
            source={
              item.coverImageURL ??
              (visual
                ? `/categoria/implantacao/${visual.folder}/main.jpg`
                : PLACEHOLDER)
            }
          />
          <Box
            sx={{
              p: 1.75,
              flex: 1,
              minHeight: 0,
            }}
          >
            <Typography
              title={item.title ?? visual?.title ?? item.description}
              sx={{
                fontWeight: 700,
                fontSize: 15,
                lineHeight: 1.3,
                color: '#000',
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.title ?? visual?.title ?? item.description}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 13,
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.shortDescription ?? visual?.description ?? item.description}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderTop: '1px solid #f0f0f0',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: DarkGreen,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #c9d8cc',
                borderRadius: 1,
              }}
            >
              {active ? 'Trocar terapia' : 'Selecionar terapia'}
            </Typography>
          </Box>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        Selecione uma terapia e um produto. A seleção é opcional e ainda não
        será salva no cadastro.
      </Typography>
      <Typography
        variant="body2"
        role="status"
        aria-live="polite"
        sx={{ color: DarkGreen }}
      >
        {selection
          ? `Produto selecionado: ${selection.description}`
          : 'Nenhum produto selecionado.'}
      </Typography>
      {categoriesLoading ? (
        <CircularProgress size={24} aria-label="Carregando terapias" />
      ) : categoriesError ? (
        <Alert
          severity="error"
          action={
            <Button onClick={() => setCategoryAttempt((value) => value + 1)}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar as terapias.
        </Alert>
      ) : therapies.length === 0 ? (
        <Typography>Nenhuma terapia disponível.</Typography>
      ) : (
        <Box
          ref={strip}
          tabIndex={0}
          role="region"
          aria-label="Terapias e produtos"
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            minWidth: 0,
            maxWidth: '100%',
            overflowX: therapy ? { xs: 'auto', sm: 'hidden' } : 'auto',
            p: therapy ? 0 : 0.5,
            pb: therapy ? 0 : 2,
          }}
        >
          {!therapy ? (
            therapies.map((item) => therapyCard(item))
          ) : (
            <>
              {therapyCard(therapy, true)}
              <Box
                ref={productStrip}
                role="region"
                tabIndex={0}
                aria-label={`Produtos de ${therapy.description}`}
                sx={{
                  display: 'flex',
                  flex: { xs: '0 0 auto', sm: '1 1 0%' },
                  minWidth: 0,
                  overflowX: { xs: 'visible', sm: 'auto' },
                  p: 0.5,
                  pb: 2,
                  gap: 1.5,
                  alignItems: 'flex-start',
                  '@keyframes equipmentReveal': {
                    from: { opacity: 0, transform: 'translateX(16px)' },
                    to: { opacity: 1, transform: 'translateX(0)' },
                  },
                  animation: reduceMotion
                    ? 'none'
                    : 'equipmentReveal 250ms ease-out',
                }}
              >
                {productGroups
                  .filter(
                    (group) =>
                      !selection ||
                      group.produtos.some(
                        (product) => product.id === selection.id
                      )
                  )
                  .map((group) => (
                    <Box
                      key={group.id}
                      ref={(node: HTMLDivElement | null) => {
                        if (node) productCards.current.set(group.id, node);
                        else productCards.current.delete(group.id);
                      }}
                      sx={{
                        width: 260,
                        height: 344,
                        flexShrink: 0,
                        display: 'flex',
                      }}
                    >
                      <SubCategoryCard
                        compact
                        currentUser={currentUser}
                        subcategory={group}
                        selectedProductId={selection?.id}
                        onSelectProduct={selectProduct}
                        onDeselectProduct={selection ? closeProduct : undefined}
                      />
                    </Box>
                  ))}
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    width: 260,
                    height: 344,
                    flexShrink: 0,
                    display:
                      loading || error || productGroups.length === 0
                        ? 'flex'
                        : 'none',
                  }}
                  aria-live="polite"
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      aria-label="Carregando produtos"
                    />
                  ) : error ? (
                    <>
                      <Typography color="error">
                        Não foi possível carregar os produtos.
                      </Typography>
                      <Button onClick={() => setAttempt((value) => value + 1)}>
                        Tentar novamente
                      </Button>
                    </>
                  ) : productGroups.length === 0 ? (
                    <Typography>
                      Nenhum produto disponível nesta terapia.
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            </>
          )}
        </Box>
      )}
      {therapy && selection && (
        <PatientEquipmentQuestionnaire
          key={`${therapy.id}:${selection.id}:${selection.sap_code}`}
          product={selection}
          patient={patient}
        />
      )}
    </Stack>
  );
};
